<!--
  Renders the "expected value" control for a rule, typed to the resolved
  condition field metadata (design skill §6 dense controls): an option
  select for enums, True/False for booleans, a number input (with a unit
  suffix) for number/duration/percentage, and a plain text input otherwise.
-->
<script setup lang="ts">
import type { HTMLAttributes } from "vue";
import { cn } from "@/lib/utils";
import type { RuleFieldMeta } from "./ruleModel";

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  modelValue: unknown;
  meta: RuleFieldMeta;
  class?: HTMLAttributes["class"];
}>();

const emit = defineEmits<{ "update:modelValue": [value: unknown] }>();

const unitLabel = computed(() => {
  if (props.meta.unit) return props.meta.unit;
  if (props.meta.valueType === "percentage") return "%";
  if (props.meta.valueType === "duration") return "seconds";
  return "";
});

function onNumberInput(value: string | number) {
  emit("update:modelValue", value === "" ? "" : Number(value));
}
</script>

<template>
  <Select
    v-if="meta.options?.length"
    :model-value="modelValue === undefined || modelValue === null ? undefined : String(modelValue)"
    @update:model-value="(value) => emit(
      'update:modelValue',
      meta.options?.find((option) => String(option.value) === value)?.value ?? value,
    )"
  >
    <SelectTrigger size="sm" :class="cn('h-8 w-full rounded-sm text-[13px]', props.class)">
      <SelectValue placeholder="Select a value" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem v-for="option in meta.options" :key="String(option.value)" :value="String(option.value)">
        {{ option.label }}
      </SelectItem>
    </SelectContent>
  </Select>

  <Select
    v-else-if="meta.valueType === 'boolean'"
    :model-value="modelValue === undefined || modelValue === null || modelValue === '' ? undefined : String(modelValue)"
    @update:model-value="(value) => emit('update:modelValue', value === 'true')"
  >
    <SelectTrigger size="sm" :class="cn('h-8 w-full rounded-sm text-[13px]', props.class)">
      <SelectValue placeholder="Select true/false" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="true">True</SelectItem>
      <SelectItem value="false">False</SelectItem>
    </SelectContent>
  </Select>

  <div v-else-if="['number', 'duration', 'percentage'].includes(meta.valueType)" :class="cn('relative', props.class)">
    <Input
      type="number"
      :model-value="modelValue as number | string"
      class="h-8 w-full rounded-sm text-[13px]"
      :class="unitLabel ? 'pr-12' : ''"
      placeholder="Value"
      @update:model-value="onNumberInput"
    />
    <span
      v-if="unitLabel"
      class="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-[11px] text-muted-foreground"
    >{{ unitLabel }}</span>
  </div>

  <Input
    v-else
    :model-value="modelValue as string"
    :class="cn('h-8 rounded-sm text-[13px]', props.class)"
    placeholder="Value"
    @update:model-value="(value) => emit('update:modelValue', value)"
  />
</template>
